// ...existing code...
import { Component, OnInit } from '@angular/core';
import { CardComponent, CardBodyComponent } from '@coreui/angular';
import { IconModule } from '@coreui/icons-angular';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { AttritionService } from '../../../services/attrition.service';


@Component({
  selector: 'app-attrition-updates',
  standalone: true,
  templateUrl: './attrition-updates.component.html',
  styleUrls: ['./attrition-updates.component.scss'],
  imports: [CardComponent, CardBodyComponent, IconModule, CommonModule, FormsModule, RouterLink]
})
export class AttritionUpdatesComponent implements OnInit {
  attritions: any[] = [];
  editingActionItemsIndex: number | null = null;
  actionItemsDraft: { attrition_id: number; action_item: string }[] = [];
  loading = false;
  activePage: number = 1;
  totalItems: number = 0;
  itemsPerPage: number = 20;
  totalPages: number = 1;
  links: any[] = [];
  readonly currentYear = new Date().getFullYear();
  selectedYear: number = this.currentYear;
  searchQuery: string = '';
  private searchTimeout: any;

  constructor(private attritionService: AttritionService) {}

  ngOnInit(): void {
    this.loadAttritions(this.activePage, this.selectedYear);
  }

  loadAttritions(page: number, year?: number, search?: string) {
    this.loading = true;
    this.attritionService.getAttritions(this.itemsPerPage, year, page, search).subscribe({
      next: (response: any) => {
        this.attritions = response?.data || [];
        this.totalItems = response?.total || 0;
        this.totalPages = response?.last_page || 1;
        this.activePage = response?.current_page || page;
        this.links = response?.links || [];
        this.loading = false;
      },
      error: () => {
        this.attritions = [];
        this.loading = false;
      }
    });
  }

  onPageChange(page: number) {
    if (page < 1 || page > this.totalPages) return;
    this.activePage = page;
    this.loadAttritions(page, this.selectedYear, this.searchQuery);
  }

  getPaginationArray(): (number | 'ellipsis')[] {
    const pages: (number | 'ellipsis')[] = [];
    if (this.totalPages <= 7) {
      for (let i = 1; i <= this.totalPages; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1);
      if (this.activePage > 4) pages.push('ellipsis');
      const start = Math.max(2, this.activePage - 2);
      const end = Math.min(this.totalPages - 1, this.activePage + 2);
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      if (this.activePage < this.totalPages - 3) pages.push('ellipsis');
      pages.push(this.totalPages);
    }
    return pages;
  }

  onItemsPerPageChange(): void {
    this.activePage = 1;
    this.loadAttritions(this.activePage, this.selectedYear, this.searchQuery);
  }

  getChunkedPagination(): (number | 'ellipsis')[] {
    const pages: (number | 'ellipsis')[] = [];
    const total = this.totalPages;
    const current = this.activePage;
    if (total <= 7) {
      for (let i = 1; i <= total; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1);
      let left = current - 2;
      let right = current + 2;
      if (left <= 2) {
        left = 2;
        right = left + 4;
      }
      if (right >= total - 1) {
        right = total - 1;
        left = right - 4;
      }
      if (left > 2) {
        pages.push('ellipsis');
      }
      for (let i = left; i <= right; i++) {
        if (i > 1 && i < total) {
          pages.push(i);
        }
      }
      if (right < total - 1) {
        pages.push('ellipsis');
      }
      pages.push(total);
    }
    return pages;
  }

  onChunkEllipsisClick(isNext: boolean): void {
    if (isNext) {
      this.onPageChange(Math.min(this.activePage + 5, this.totalPages));
    } else {
      this.onPageChange(Math.max(this.activePage - 5, 1));
    }
  }

  onYearChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    let year = Number(input.value);
    if (!isNaN(year)) {
      if (year < 2025) year = 2025;
      if (year > this.currentYear) year = this.currentYear;
      input.value = String(year);
      this.selectedYear = year;
      this.activePage = 1;
      this.loadAttritions(this.activePage, this.selectedYear, this.searchQuery);
    }
  }

  onSearchChange(): void {
    clearTimeout(this.searchTimeout);
    this.searchTimeout = setTimeout(() => {
      this.activePage = 1;
      this.loadAttritions(1, this.selectedYear, this.searchQuery);
    }, 400);
  }

  getPageEnd(): number {
    return Math.min(this.activePage * this.itemsPerPage, this.totalItems);
  }

  expandedIndex: number | null = null;
  toggleRow(i: number) {
    this.expandedIndex = this.expandedIndex === i ? null : i;
    if (this.expandedIndex !== null) {
      this.editingActionItemsIndex = null;
    }
  }

  startEditActionItems(i: number) {
    this.editingActionItemsIndex = i;
    const attritionId = this.attritions[i].id;
    this.actionItemsDraft = this.attritions[i].action_items
      ? [...this.attritions[i].action_items.map((item: any) => ({
          attrition_id: attritionId,
          action_item: item.action_item || ''
        }))]
      : [];
  }

  cancelEditActionItems() {
    this.editingActionItemsIndex = null;
    this.actionItemsDraft = [];
  }

  addActionItem() {
    // Add new item with attrition_id and action_item
    const attritionId = this.editingActionItemsIndex !== null ? this.attritions[this.editingActionItemsIndex].id : null;
    if (attritionId) {
      this.actionItemsDraft.push({ attrition_id: attritionId, action_item: '' });
    }
  }

  removeActionItem(idx: number) {
    this.actionItemsDraft.splice(idx, 1);
  }

  saveActionItems(i: number) {
    // Call service to update action items for attrition[i]
    const updated = { ...this.attritions[i], action_items: this.actionItemsDraft };
    this.attritionService.updateAttrition(updated.id, updated).subscribe({
      next: (response) => {
        this.attritions[i].action_items = [...this.actionItemsDraft];
        this.editingActionItemsIndex = null;
        this.actionItemsDraft = [];
      },
      error: (err) => {
        // Optionally show error
        console.error('Failed to update action items', err);
      }
    });
  }
}
